import type { UnknownAction } from '@reduxjs/toolkit';
import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { tradingGroupJobsActions } from '@skynet/data-trading-groups-jobs';
import type { TradingGroupJobOperation } from '@skynet/data-trading-groups-jobs';
import { TELEGRAM_GROUPS_API_ENDPOINTS as cfg } from './config';

/**
 * The `202 + { jobId }` contract (SKY-4445) is deployed, but guard against a
 * response with no usable `jobId` anyway — tracking `undefined` as a literal
 * jobId would be a silent, permanently-pending row and a false success toast
 * once the stale-job backstop fires.
 */
const isValidJobId = (jobId: unknown): jobId is string =>
  typeof jobId === 'string' && jobId.length > 0;

/** The group(s) a mutation's `202` response was expected to affect. */
interface AffectedGroups {
  groupId?: number;
  groupIds?: number[];
}

/**
 * Handles a `202` (or, on the still-live synchronous contract, a `200`/`204`)
 * response with no usable `jobId` — there is no job to track, but the
 * mutation may well have already applied (that's exactly the shape the old,
 * still-deployed contract returns). Rather than a silent no-op:
 * - invalidate the affected cache so a mutation that did apply isn't left
 *   stale until an unrelated refetch, and
 * - settle a synthetic job as `outcome: 'unknown'` so the existing
 *   notification UI surfaces it with the same neutral "status unknown, please
 *   check the group's current state" messaging the stale-job backstop uses —
 *   we genuinely don't know whether it succeeded.
 */
const trackMissingJobId = (
  dispatch: (action: UnknownAction) => unknown,
  endpointName: string,
  operation: TradingGroupJobOperation,
  affected: AffectedGroups,
  data: unknown,
) => {
  console.error(`${endpointName}: response missing a jobId`, data);

  const { groupId, groupIds } = affected;
  const jobId = `missing-job-id:${crypto.randomUUID()}`;
  dispatch(tradingGroupJobsActions.jobStarted({ jobId, operation, groupId, groupIds }));
  dispatch(tradingGroupJobsActions.jobSettled({ jobId, outcome: 'unknown', groupId, groupIds }));

  const ids = groupIds ?? (groupId !== undefined ? [groupId] : []);
  dispatch(
    telegramGroupsApi.util.invalidateTags(
      ids.length > 0
        ? [
            ...ids.map((id) => ({ type: 'TelegramGroups' as const, id })),
            { type: 'TelegramGroups', id: LIST_TAG_ID },
          ]
        : ['TelegramGroups'],
    ),
  );
};

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `TelegramGroups` tag type covers both the collection and individual
 * entities. The list/get queries provide the tags; nothing here invalidates
 * them anymore — create/update/archive/delete now resolve with `202` +
 * `{ jobId }` (SKY-4445), so a `200`-shaped mutation no longer means the
 * mutation actually applied. Invalidation instead happens once the
 * assistant WS channel's `JobCompleted` frame confirms the job finished (see
 * the dashboard's `ws-assistant-effects`); `onQueryStarted` below only starts
 * tracking the job (`@skynet/data-trading-groups-jobs`) as soon as it's
 * accepted, so a row/dialog can show a pending state for the job's whole
 * lifetime instead of just the `202` round-trip.
 */
export const telegramGroupsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['TelegramGroups'],
  endpoints: {
    [cfg.getTelegramGroupMembers.generated]: {
      // Own tag id, deliberately distinct from `{ type: 'TelegramGroups', id:
      // arg.id }` (`getTelegramGroup`'s own tag): an unrelated group edit
      // (rename, accountType) would otherwise also refetch the live Telegram
      // membership, which can take up to 30s. Not driven by WS invalidation —
      // the members list is refetched explicitly once this admin's own
      // add/remove job settles (`TradingGroupMembersTab`), per the ticket's
      // "membership lives only on Telegram, so a stale list is the default
      // state."
      providesTags: (_result, _error, arg) => [{ type: 'TelegramGroups', id: `${arg.id}-members` }],
    },
    [cfg.addTelegramGroupMembers.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const userIds = arg.addTelegramGroupMembersBody.userIds;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'addTelegramGroupMembers',
              'add-members',
              { groupId: arg.id },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'add-members',
              groupId: arg.id,
              userIds,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400/503/403) — nothing was
          // enqueued to track; the synchronous 400s render inline on the
          // form instead (see `member-result.ts`'s doc comment).
        }
      },
    },
    [cfg.removeTelegramGroupMembers.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const userIds = arg.removeTelegramGroupMembersBody.userIds;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'removeTelegramGroupMembers',
              'remove-members',
              { groupId: arg.id },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'remove-members',
              groupId: arg.id,
              userIds,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400/403/404) — nothing was
          // enqueued to track; the synchronous 400s render inline on the
          // form instead.
        }
      },
    },
    [cfg.createTelegramGroup.generated]: {
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(dispatch, 'createTelegramGroup', 'create', {}, data);
            return;
          }

          dispatch(tradingGroupJobsActions.jobStarted({ jobId, operation: 'create' }));
        } catch {
          // Rejected before reaching 202 (e.g. 400/403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.listTelegramGroups.generated]: {
      providesTags: (result) => provideListTags(result, 'TelegramGroups'),
    },
    [cfg.getTelegramGroup.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'TelegramGroups', id: arg.id }],
    },
    [cfg.archiveTelegramGroup.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'archiveTelegramGroup',
              'archive',
              { groupId: arg.id },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'archive',
              groupId: arg.id,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 404/403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.updateTelegramGroup.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(dispatch, 'updateTelegramGroup', 'update', { groupId: arg.id }, data);
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'update',
              groupId: arg.id,
              // Present only for a rename (SKY-4546) — carried through to the
              // settled notification so a failure's Retry action can
              // resubmit the exact same title (the worker operation is
              // idempotent: nothing is persisted on failure).
              title: arg.updateTelegramGroupBody.title,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 404/403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.changeTelegramGroupBot.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'changeTelegramGroupBot',
              'change-bot',
              { groupId: arg.id },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'change-bot',
              groupId: arg.id,
              botUsername: arg.changeTelegramGroupBotBody.botUsername,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400/403/404) — nothing was enqueued to track.
        }
      },
    },
    [cfg.deleteTelegramGroup.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(dispatch, 'deleteTelegramGroup', 'delete', { groupId: arg.id }, data);
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'delete',
              groupId: arg.id,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400/403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.bulkArchiveTelegramGroups.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const groupIds = arg.bulkArchiveTelegramGroupsBody.ids;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'bulkArchiveTelegramGroups',
              'bulk-archive',
              { groupIds },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({ jobId, operation: 'bulk-archive', groupIds }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400 over-cap/empty/duplicate ids, 403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.bulkDeleteTelegramGroups.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const groupIds = arg.bulkDeleteTelegramGroupsBody.ids;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'bulkDeleteTelegramGroups',
              'bulk-delete',
              { groupIds },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({ jobId, operation: 'bulk-delete', groupIds }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400 over-cap/empty/duplicate ids, 403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.bulkAddTelegramGroupMembers.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // `groupIds` is absent for a `scope`-targeted job — resolved
        // server-side, never known to this client up front (SKY-4559).
        const { userIds, groupIds } = arg.bulkAddTelegramGroupMembersBody;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'bulkAddTelegramGroupMembers',
              'bulk-add-members',
              { groupIds },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'bulk-add-members',
              groupIds,
              userIds,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400 target-required/over-cap, 403) — nothing was enqueued to track.
        }
      },
    },
    [cfg.bulkRemoveTelegramGroupMembers.generated]: {
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const { userIds, groupIds } = arg.bulkRemoveTelegramGroupMembersBody;

        try {
          const { data } = await queryFulfilled;
          const jobId = data?.jobId;

          if (!isValidJobId(jobId)) {
            trackMissingJobId(
              dispatch,
              'bulkRemoveTelegramGroupMembers',
              'bulk-remove-members',
              { groupIds },
              data,
            );
            return;
          }

          dispatch(
            tradingGroupJobsActions.jobStarted({
              jobId,
              operation: 'bulk-remove-members',
              groupIds,
              userIds,
            }),
          );
        } catch {
          // Rejected before reaching 202 (e.g. 400 target-required/over-cap/scope-empty, 403) — nothing was enqueued to track.
        }
      },
    },
  },
});

export const hookExports: HookExports<typeof cfg, typeof telegramGroupsApi> = createHookExports(
  cfg,
  telegramGroupsApi,
);

export const telegramGroupsApiEndpoints = createEndpointExports(cfg, telegramGroupsApi);
